import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { createDecision } from "../../../services/decisionService";

interface CreateDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateDecisionModal = ({ isOpen, onClose }: CreateDecisionModalProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Decision name is required");
      return;
    }

    setIsCreating(true);
    try {
      const decision = await createDecision({
        name: name.trim(),
        description: description.trim() || undefined,
      });

      toast.success("Decision created successfully");
      onClose();
      
      // Navigate to the decision designer
      if (decision.nameSlug) {
        navigate(`/studio/decisions/${decision.nameSlug}`);
      }
    } catch (error) {
      console.error("Error creating decision:", error);
      toast.error("Failed to create decision");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setName("");
      setDescription("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create New Decision</h2>
          <button
            onClick={handleClose}
            disabled={isCreating}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label htmlFor="decision-name" className="block text-sm font-medium text-gray-700 mb-1">
                Decision Name *
              </label>
              <input
                id="decision-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter decision name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isCreating}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="decision-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="decision-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter decision description (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                disabled={isCreating}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !name.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? "Creating..." : "Create Decision"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDecisionModal;
